import { useState } from "react";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarDays, Info } from "lucide-react";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { activityApi, type ActivityLogEntry } from "@/lib/api/services/activity.api";
import { activityRowsToCsv } from "@/components/activity/activity-format";
import { Field } from "./profile-form";

/** Parse a `DD.MM.YYYY` (or `/`,`-`) string to an ISO timestamp, or undefined. */
function parseDmy(value: string, endOfDay = false): string | undefined {
  const match = value.trim().match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (!match) return undefined;
  const [, dd, mm, yyyy] = match;
  const date = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

async function fetchActivity(from?: string, to?: string): Promise<ActivityLogEntry[]> {
  const rows: ActivityLogEntry[] = [];
  for (let page = 1; page <= 20; page += 1) {
    const res = await activityApi.paginate({ from, to, page, limit: 100 });
    rows.push(...res.data);
    if (!res.meta.hasNext) break;
  }
  return rows;
}

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

function DateInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="DD.MM.YYYY"
        className={cn(controlClass, "pr-10")}
      />
      <CalendarDays className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-(--color-text-secondary)" />
    </div>
  );
}

export function ExportActivityLogDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useLingui();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [format, setFormat] = useState("csv");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const rows = await fetchActivity(parseDmy(from), parseDmy(to, true));
      const isJson = format === "json";
      const content = isJson
        ? JSON.stringify(rows, null, 2)
        : activityRowsToCsv(rows, i18n);
      const blob = new Blob([content], {
        type: isJson ? "application/json" : "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `activity-log.${isJson ? "json" : "csv"}`;
      anchor.click();
      URL.revokeObjectURL(url);
      chipToast({
        message: t`Exported ${plural(rows.length, {
          one: "# activity entry",
          other: "# activity entries",
        })}.`,
      });
      onOpenChange(false);
    } catch {
      chipToast({ message: t`Couldn't export the activity log. Please try again.` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Export activity log</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Export your recent audit trail for review, compliance, or record
              keeping.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Data range</Trans>
            </span>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t`From`} htmlFor="export-from">
                <DateInput id="export-from" value={from} onChange={setFrom} />
              </Field>
              <Field label={t`To`} htmlFor="export-to">
                <DateInput id="export-to" value={to} onChange={setTo} />
              </Field>
            </div>
          </div>

          <Field label={t`Format`}>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className={controlClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="flex items-start gap-3 rounded-lg bg-(--color-surface-muted) p-3">
            <Info className="mt-0.5 size-5 shrink-0 text-(--color-text-secondary)" />
            <div>
              <p className="text-sm font-medium text-(--color-text-primary)">
                <Trans>Please note</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  Export includes timestamps, event types, session details, and
                  affected settings.
                </Trans>
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button type="button" onClick={handleExport} disabled={busy}>
            {busy ? t`Exporting…` : t`Export log`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
